import { useCallback, useEffect, useMemo } from "react";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { isTeleconsultoriaInviteTask } from "../../invites/helpers/inviteTaskHelpers";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import { isWithinDateFilter } from "../../shared/helpers/dateRange";
import type { TeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { listAcceptedInviteRows } from "../services/acceptedInvitesService";
import { listBookedTeleconsultorias } from "../services/bookedTeleconsultoriaService";
import { mergeBookedRows } from "../transformers/bookedRowTransformers";
import type { BookedTeleconsultoriaRow } from "../types";

const EMPTY_ROWS: BookedTeleconsultoriaRow[] = [];

/**
 * "Próximas teleconsultorias" do profissional da PRESTADORA, no período do filtro:
 * (a) consultas nas minhas agendas de teleconsultoria;
 * (b) convites que aceitei (Tasks `accepted` para mim), mesmo fora das minhas agendas.
 * Recarrega quando um convite é aceito (contexto de tasks/SSE).
 */
export function useUpcomingTeleconsultorias(range: TeleconsultoriaDateRange) {
  const { access, user } = useSessionContext();
  const { profile } = useTeleconsultoriaProfile();
  const { allTasks } = useTaskWatchingContext();
  const organizationToken = profile.kind === "provider" ? profile.organizationToken : "";
  const practitionerReference = `Practitioner/${oids.cpf}-${user?.username}`;
  const myRoleReference = `PractitionerRole/${access?.organizationId}-${oids.cpf}-${user?.username}`;

  const acceptedInvites = useMemo<fhir5.Task[]>(
    () =>
      (Array.isArray(allTasks) ? allTasks : []).filter(
        (task) =>
          isTeleconsultoriaInviteTask(task) &&
          task.status === "accepted" &&
          task.for?.reference === myRoleReference,
      ),
    [allTasks, myRoleReference],
  );
  const acceptedKey = acceptedInvites.map((task) => task.id).join(",");
  const { startDate, endDate, date, mode } = range;

  const loadTask = useAsyncTask({
    task: useCallback(async () => {
      const [agendaRows, inviteRows] = await Promise.all([
        listBookedTeleconsultorias({ organizationToken, practitionerReference, startDate, endDate }),
        listAcceptedInviteRows(acceptedInvites),
      ]);
      // Convites aceitos vêm sem filtro de data: aplica o período aqui.
      const inviteRowsInRange = inviteRows.filter((row) => isWithinDateFilter(row.start, date, mode));
      return mergeBookedRows(agendaRows, inviteRowsInRange);
      // acceptedKey representa acceptedInvites (evita recriar a cada render do contexto).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationToken, practitionerReference, startDate, endDate, acceptedKey]),
    initialData: EMPTY_ROWS,
  });

  const { run } = loadTask;
  const reload = useCallback(() => {
    void run().catch((error) => console.error("useUpcomingTeleconsultorias", error));
  }, [run]);

  useEffect(() => {
    if (organizationToken) reload();
  }, [organizationToken, reload]);

  return {
    rows: loadTask.data ?? EMPTY_ROWS,
    isLoading: loadTask.loading,
    hasError: loadTask.status === "error",
    reload,
  };
}
